/**
 * The value carried by one column filter entry.
 *
 * Historically this was always a plain array of strings ("include these
 * values") or a scalar (text / date filters). Advanced filters can now
 * also exclude, so their value may instead be
 * `{ include: string[], exclude: string[] }`.
 *
 * Both shapes stay in circulation on purpose:
 *
 *   - session storage holds whatever was written before this change, so
 *     a plain array must keep meaning "include these";
 *   - every other filter type still writes arrays and scalars, and none
 *     of them should have to learn about the object shape.
 *
 * The object shape carries the two lists in ONE filter entry rather than
 * two, because the API answers 422 to two terms for the same field, and
 * `getEndpoint` serialises one entry per term. One entry also keeps the
 * table's own machinery — MRT's per-column value, `onFilter`'s diffing,
 * the session-storage round trip — unchanged.
 */

export const EMPTY_FILTER_VALUE = { include: [], exclude: [] };

const asList = value => (Array.isArray(value) ? value.filter(item => item !== '') : []);

/** True for the `{ include, exclude }` shape, false for arrays and scalars. */
export const isSplitFilterValue = value =>
  Boolean(value) && !Array.isArray(value) && typeof value === 'object';

/**
 * Reads any filter value as `{ include, exclude }`.
 *
 * A plain array reads as include-only, which is what every value written
 * before exclusion existed means. A scalar (text, date) has no notion of
 * either list and reads as empty — callers that handle scalars must
 * check for them before calling this.
 */
export const toSplitFilterValue = value => {
  if (isSplitFilterValue(value)) {
    return { include: asList(value.include), exclude: asList(value.exclude) };
  }
  return { include: asList(value), exclude: [] };
};

/** Nothing selected in either list. */
export const isEmptyFilterValue = value => {
  const { include, exclude } = toSplitFilterValue(value);
  return include.length === 0 && exclude.length === 0;
};

/**
 * Collapses back to a plain array when nothing is excluded.
 *
 * Keeps stored filters and outgoing requests identical to what they were
 * before exclusion existed for the overwhelmingly common include-only
 * case — so this feature cannot change behaviour for a filter nobody
 * excluded anything in.
 */
export const fromSplitFilterValue = ({ include = [], exclude = [] }) =>
  exclude.length === 0 ? [...include] : { include: [...include], exclude: [...exclude] };

/** True when a filter entry's value actually narrows a table. */
export const hasFilterValue = v => {
  if (v == null) return false;
  if (Array.isArray(v)) return v.some(x => x !== '' && x != null);
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
};
